/**
 * This file acts as a translation layer between the ASP.NET model that's injected into the window and the model that we want to use with the rest of the app
 * In many cases, the translation will be simply lowercasing the property names, but it's cleaner to map these properties in one place rather than having
 * inconsistent variable-name styles throughtout the app
 */
import groupBy from 'lodash.groupby';
import uniqBy from 'lodash.uniqby';
import sortBy from 'lodash.sortby';
import { getLocalizedText as text } from '@vp-dst/localization-util';

import DesignServicesUtil from 'utils/DesignServicesUtil';
import { siteCulture } from 'settings/vistaprintContext';

const { model } = window;

const designLivePriceIncreaseQty = 2;

// IL-105 DesignLive price test
const isInDesignLivePriceTest = window.utag_data.ActiveFeatureValues.indexOf('30391') >= 0;

// Using MSR to control exposure for IL-185, expanding LogoDesign to FR and DE locales
const logoDesignLocalizationAvailable = window.utag_data.ActiveFeatureValues.indexOf('30422') >= 0;

export default class DSHubModel {
  static get bundleLocation() {
    return model.BundleLocation;
  }

  static get productGroups() {
    const graphicDesignProductGroups = model.ProductGroups.map(productGroup => ({
      localizedName: productGroup.Name,
      products: productGroup.ProductSelections.map(productSelection => ({
        localizedName: productSelection.Name,
        pfid: productSelection.Pfid,
        localizedPriceHTML: this.getServicePriceHTMLByPFID('BKC', productSelection.DifyServiceQty), // todo: is BKC always the right choice?
        priceMultiplier: productSelection.DifyServiceQty,
        requestURL: DesignServicesUtil.getProductDesignBriefURL({ productName: productSelection.Name, pfid: productSelection.Pfid }),
      })),
    }));

    const logoProducts = [];

    if (this.showLogoDesignService) {
      const lds = {
        localizedName: text('unique-logo-design'),
        pfid: 'BY2',
        localizedPriceHTML: this.logoDesignServicePriceHTML,
        requestURL: DesignServicesUtil.getLogoDesignBriefURL(),
      };
      logoProducts.concat(lds);
    }

    if (this.showLogoMakerService) {
      const lms = {
        localizedName: text('diy-logo-maker'),
        pfid: 'CV6',
        localizedPriceHTML: this.freePriceHTML,
        requestURL: DesignServicesUtil.getLogoMakerURL(),
      };
      logoProducts.concat(lms);
    }

    const logoDesignProductGroup = {
      localizedName: text('logo'),
      products: logoProducts,
    };


    return [...graphicDesignProductGroups, logoDesignProductGroup];
  }

  static get allProducts() {
    return this.productGroups.reduce((acc, productGroup) => [...acc, ...productGroup.products], []);
  }

  static get productsByPriceMultiplier() {
    const uniqueProductsWithPriceMultipliers = sortBy(uniqBy(this.allProducts, product => product.localizedName), product => product.localizedName)
      .filter(product => product.priceMultiplier !== undefined);

    return groupBy(uniqueProductsWithPriceMultipliers, product => product.priceMultiplier);
  }

  static get freePriceHTML() {
    return `<span class="discount-price"><span class="undefined-price">${text('free')}</span></span>`;
  }

  static getServicePriceHTMLByPFID(pfid, quantity = 1) {
    if (quantity === 0) { // todo: fix this garb
      return this.freePriceHTML;
    }

    const priceHtmlsByQuantity = model.ProductDesignPriceHtmlByQuantityByPfid[pfid];

    if (!priceHtmlsByQuantity) {
      throw new Error(`Prices not found for pfid ${pfid}`);
    }

    const priceHtml = priceHtmlsByQuantity[quantity];

    if (!priceHtml) {
      throw new Error(`Quantity ${quantity} not found for pfid ${pfid}`);
    }

    return priceHtml;
  }

  static getDesignPriceHTMLByPFID(pfid) {
    const product = this.allProducts.find(p => p.pfid === pfid);

    return product ? product.localizedPriceHTML : undefined;
  }

  static get logoDesignServicePriceHTML() {
    return this.getServicePriceHTMLByPFID('BY2');
  }

  static get productDesignServicePriceHTML() {
    return this.getServicePriceHTMLByPFID('BKC');
  }

  static get designLiveServicePriceHTML() {
    // IL-105 price should be 10$ more than product design in test cell
    if (!isInDesignLivePriceTest) {
      return this.getServicePriceHTMLByPFID('BKC');
    }
    return this.getServicePriceHTMLByPFID('BKC', (1 + designLivePriceIncreaseQty));
  }

  static get websiteDesignPriceHTML() {
    return this.getServicePriceHTMLByPFID('CEU');
  }

  static get logoMakerDownloadPriceHTML() {
    return this.getServicePriceHTMLByPFID('CV6');
  }

  static get showLogoDesignService() {
    return (siteCulture.substring(0, 2).toLowerCase() === 'en' || logoDesignLocalizationAvailable);
  }

  static get showLogoMakerService() {
    return false;
  }

  static get showWebsiteDesignService() {
    return (siteCulture.substring(0, 2).toLowerCase() === 'en');
  }

  static get showSocialMediaDesign() {
    return window.model.SocialMediaDesignEnabled;
  }

  static getSearchExamples() {
    return (this.showLogoDesignService || this.showLogoMakerService)
      ? [text('business-cards'), text('postcards'), text('banners'), text('invitations'), text('flyers')]
      : [text('business-cards'), text('logo'), text('postcards'), text('banners'), text('invitations'), text('flyers')];
  }

  static get isCoDesignEnabled() {
    return model.IsCoDesignEnabled;
  }
}



// WEBPACK FOOTER //
// ./src/models/DSHubModel.js